"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Clock, Loader, PhoneCall, ShieldCheck, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormItem, FormField, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const benefits = [{
  icon: ShieldCheck,
  title: "Architectural Precision",
  desc: "Engineered specifically for Calgary's climate, severe hail, and high wind loads."
}, {
  icon: Clock,
  title: "Fast 24-Hour Turnaround",
  desc: "Receive a transparent, fully itemized digital estimate within 1 business day."
}, {
  icon: Sparkles,
  title: "Premium Materials",
  desc: "Specializing in standing seam metal roofing, ACM cladding, and James Hardie siding."
}];
function EstimateFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border border-primary/30 rounded-lg text-center space-y-4">
        <CheckCircle2 className="size-12 text-primary mx-auto" />
        <h3 className="text-xl font-bold font-default text-foreground">
          Estimate Requested!
        </h3>
        <p className="text-sm text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Name</FormLabel>
            <FormControl>
              <Input placeholder="John Smith" autoComplete="name" className="w-full bg-background/60 border-border focus:border-primary h-12 sm:h-11 text-base sm:text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone_number" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Phone</FormLabel>
            <FormControl>
              <Input type="tel" inputMode="tel" autoComplete="tel" placeholder="(403) 555-0123" className="w-full bg-background/60 border-border focus:border-primary h-12 sm:h-11 text-base sm:text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      validate: val => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) || "Invalid email address"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Email (optional)</FormLabel>
            <FormControl>
              <Input type="email" inputMode="email" autoComplete="email" placeholder="john@example.com" className="w-full bg-background/60 border-border focus:border-primary h-12 sm:h-11 text-base sm:text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="postal_code" rules={{
      required: "Postal code is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Postal Code</FormLabel>
            <FormControl>
              <Input placeholder="T2P 1J9" inputMode="text" autoCapitalize="characters" autoComplete="postal-code" className="w-full bg-background/60 border-border focus:border-primary h-12 sm:h-11 text-base sm:text-sm uppercase" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Service Needed</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full bg-background/60 border-border focus:border-primary h-12 sm:h-11 text-base sm:text-sm">
                  <SelectValue placeholder="Select service needed" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Roofing">Roofing</SelectItem>
                <SelectItem value="Siding">Siding</SelectItem>
                <SelectItem value="Roofing & Siding">Roofing & Siding</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-xs text-destructive">{submitError}</p>
        </div>}

      <p className="text-xs text-muted-foreground">
        We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
        <Link to="/PrivacyPolicyPage" className="text-primary underline underline-offset-2 hover:text-primary/80">
          Privacy Policy
        </Link>
        .
      </p>

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold mt-4 h-12 text-base">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Estimate...
          </> : "Get My Free Estimate"}
      </Button>
    </Form>;
}
export default function Estimate() {
  return <section id="getyourfreeestimate" className="bg-background relative ![padding:40px_0px_65px_0px]">
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20">
              <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">REQUEST AN ESTIMATE</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight leading-tight">
              Ready to Upgrade Your Property with{" "}
              <span className="text-primary">Premium Exterior</span> Renovations?
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
              MetalLine Exteriors delivers high-end metal roofing, architectural ACM cladding,
              and luxury siding engineered specifically for Calgary, Airdrie, Okotoks, Cochrane,
              and Chestermere homes.
            </p>

            <div className="space-y-4 pt-2">
              {benefits.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={i} data-index={i} className="flex items-start gap-4 p-3 rounded-lg bg-card/50 border border-border/50">
                    <div className="p-2 rounded-md bg-primary/10 text-primary shrink-0 mt-0.5">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-default text-foreground text-sm sm:text-base">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/80 font-semibold h-12 px-6">
                <Link to="tel:5878884223" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call &nbsp; (587) 888-4223</span>
                </Link>
              </Button>

              <p className="text-xs text-muted-foreground self-center text-center sm:text-left">
                Prefer to talk now? Speak directly with our Calgary exterior specialists.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5">
            <div className="backdrop-blur-md bg-card/90 border border-border/80 shadow-xl rounded-xl p-6 sm:p-8 relative">
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold font-default text-foreground">Get My Free Estimate&nbsp;</h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">Complete the form below to receive your detailed renovation quote</p>
              </div>

              <FormProvider formId="free_estimate_form_home" formKey="free_estimate_form_home" sectionName="get-your-free-estimate" defaultValues={{
              full_name: "",
              phone_number: "",
              email_address: "",
              project_type: "",
              postal_code: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              phone_number: {
                required: true
              },
              project_type: {
                required: true
              },
              postal_code: {
                required: true
              },
              email_address: {
                pattern: "email"
              }
            }} submitText="Get My Free Estimate" successMessage="Thank you! Your request has been submitted successfully. We will contact you shortly." errorMessage="Sorry, we couldn't submit your request. Please try again or call us at (587) 888-4223.">
                <EstimateFormInner />
              </FormProvider>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}